const OPTICAL_SIZE_BASE_URL = 'https://fonts.gstatic.com/s/i/short-term/release/materialsymbols';

/**
 * Retourne la taille optique Material Symbols (20, 24, 40, 48) la plus proche
 * de la taille d'affichage en pixels.
 */
const getOpticalSize = (displayPx) => {
  if (!displayPx || displayPx <= 0) return 24;
  if (displayPx <= 22) return 20;
  if (displayPx <= 32) return 24;
  if (displayPx <= 44) return 40;
  return 48;
};

const buildIconUrl = (style, icon, variant, opticalSize) =>
  `${OPTICAL_SIZE_BASE_URL}${style}/${icon}/${variant}/${opticalSize}px.svg`;

/*
  MIMOYA perf (30/09/2026) : plus aucune mesure au branchement de l'icône.
  Avant, chaque <theme-icon> lisait sa taille (getComputedStyle) dès l'exécution du script, puis
  réécrivait son URL : le navigateur recalculait les styles de toute la page, une fois par icône
  (135 sur la fiche KP, ~0,35 s de « recalcul forcé » dans PageSpeed mobile), avant le premier
  affichage. Désormais :
  - l'URL posée par snippets/icon.liquid sert telle quelle au premier rendu ;
  - un seul ResizeObserver pour toutes les icônes (au lieu d'un par icône) corrige la taille optique
    après la mise en page et avant l'affichage : toutes les lectures, puis toutes les écritures ;
  - une icône cachée ou pas encore dessinée (tiroir fermé, section calculée à l'approche) n'est
    mesurée qu'à son apparition.
  Même résultat visuel qu'avant.
*/
class ThemeIcon extends HTMLElement {
  static #observer = null;

  static #onResize(entries) {
    const updates = [];
    for (const entry of entries) {
      const icon = entry.target;
      if (!(icon instanceof ThemeIcon) || !icon.isConnected) continue;
      // Icône cachée (taille nulle) : mesurée quand elle apparaît, l'observateur est rappelé à ce moment-là.
      if (!entry.contentRect.width && !entry.contentRect.height) continue;
      const opticalSize = getOpticalSize(icon.#getDisplaySize());
      if (opticalSize !== icon.#currentOpticalSize) updates.push([icon, opticalSize]);
    }
    updates.forEach(([icon, opticalSize]) => icon.#applyOpticalSize(opticalSize));
  }

  #currentOpticalSize = null;

  connectedCallback() {
    if (this.#currentOpticalSize === null) {
      const renderedUrl = this.style.getPropertyValue('--icon-url');
      const match = renderedUrl.match(/\/(\d+)px\.svg/);
      if (match) this.#currentOpticalSize = Number(match[1]);
    }

    if (!ThemeIcon.#observer) {
      ThemeIcon.#observer = new ResizeObserver((entries) => ThemeIcon.#onResize(entries));
    }
    ThemeIcon.#observer.observe(this);
  }

  disconnectedCallback() {
    ThemeIcon.#observer?.unobserve(this);
  }

  #getDisplaySize() {
    const fontSize = parseFloat(getComputedStyle(this).fontSize);
    return Number.isFinite(fontSize) ? fontSize : 0;
  }

  #applyOpticalSize(opticalSize) {
    this.#currentOpticalSize = opticalSize;
    const { iconStyle, iconName, iconVariant } = this.dataset;
    if (!iconStyle || !iconName || !iconVariant) return;

    const url = buildIconUrl(iconStyle, iconName, iconVariant, opticalSize);
    this.style.setProperty('--icon-url', `url('${url}')`);
  }
}

if (!customElements.get('theme-icon')) {
  customElements.define('theme-icon', ThemeIcon);
}
